import type { Dossier, ContentAudit } from '@/api/client'

const PRIORITY_ORDER = ['hoog', 'gemiddeld', 'laag'] as const
const PRIORITY_LABELS: Record<string, string> = {
  hoog: 'Hoge prioriteit',
  gemiddeld: 'Gemiddelde prioriteit',
  laag: 'Lage prioriteit',
}

// Sitewide-regression thresholds — same numbers as the S23 rendering-
// regression gate (crawl_regression.py), kept consistent so "sitewide" means
// the same thing everywhere in this app, not a different judgement call per
// feature that happens to look at similar symptoms.
const SITEWIDE_MIN_COUNT = 3
const SITEWIDE_MIN_FRACTION = 0.3

interface FailedFetchGroup {
  reason: string
  urls: string[]
}

/**
 * Build the Claude Code prompt for the Content-audit tab. Same conventions
 * as generateSeoPrompt/generateVulnerabilityPrompt: root-cause-first when a
 * failure pattern spans multiple pages, then the concrete per-page action
 * items — here sourced from what the LLM itself already proposed per
 * successful audit (results.claude_code_instructions), deduped to the
 * latest audit per page (a page re-audited weekly keeps piling up history;
 * only the newest verdict is still true — same fix as the "Claude" tab,
 * DossierDetail.tsx's allClaudeInstructions).
 */
export function generateContentAuditPrompt(dossier: Dossier, audits: ContentAudit[]): string {
  const lines: string[] = []

  // audits is already newest-first (API orders by created_at desc).
  const latestByPage = new Map<string, ContentAudit>()
  for (const a of audits) {
    if (!latestByPage.has(a.page_url)) latestByPage.set(a.page_url, a)
  }
  const latest = Array.from(latestByPage.values())

  const failedFetch = latest.filter(a => a.status === 'failed_fetch')
  const completed = latest.filter(a => a.status === 'completed' && a.results?.claude_code_instructions?.length)

  if (!failedFetch.length && !completed.length) {
    return `Geen content-audit-bevindingen voor ${dossier.name}. Niets te doen.`
  }

  lines.push(`Verwerk de content-audit-bevindingen voor ${dossier.name} (${dossier.url}).`)
  lines.push('')

  // ── failed_fetch: group by reason, root-cause-first when it's sitewide ──
  if (failedFetch.length) {
    const groups = new Map<string, FailedFetchGroup>()
    for (const a of failedFetch) {
      // error_message is e.g. "empty_body: 7 woorden (<100)" or "fetch_failed: ..." —
      // bucket on the part before ":" so the same root cause groups together.
      const reason = (a.error_message ?? 'onbekend').split(':')[0].trim()
      if (!groups.has(reason)) groups.set(reason, { reason, urls: [] })
      groups.get(reason)!.urls.push(a.page_url)
    }

    for (const { reason, urls } of groups.values()) {
      // 'rendering_regression' means the backend's own gate (S24 Task 1,
      // content_audit_tasks.py::_active_regression_gate) already confirmed
      // this against site_crawl's verdict — no need to re-derive sitewide-ness
      // here, unlike 'empty_body' which is content-audit's own independent
      // signal and still needs the local count/fraction check below.
      const isSitewide =
        reason === 'rendering_regression' ||
        urls.length >= SITEWIDE_MIN_COUNT ||
        urls.length / latest.length >= SITEWIDE_MIN_FRACTION

      if ((reason === 'empty_body' || reason === 'rendering_regression') && isSitewide) {
        lines.push(`🔴 RENDERING-REGRESSIE ACTIEF — LEES DIT EERST`)
        lines.push('')
        lines.push(
          `${urls.length} pagina's leveren een (vrijwel) lege body (< 100 woorden) terwijl ` +
          `de \`<title>\` wel correct meekomt — dit wijst op een crash die middenin de ` +
          `paginarender optreedt (bv. een gedeelde include/partial/template die faalt), ` +
          `niet op ontbrekende content per pagina.`
        )
        lines.push('')
        lines.push(
          `Zoek éérst de gemeenschappelijke oorzaak (bekijk wat deze pagina's delen: ` +
          `dezelfde layout/include/component) vóór je iets per pagina aanpast. ` +
          `Zet GEEN 404/410-status op deze URL's en de-indexeer ze niet — de content ` +
          `bestaat, ze rendert alleen niet.`
        )
        lines.push('')
        lines.push('Getroffen pagina\'s:')
        for (const u of urls) lines.push(`- ${u}`)
        lines.push('')
        lines.push('---')
        lines.push('')
      } else {
        lines.push(`## ⚠️ Ophalen mislukt: ${reason} (${urls.length})`)
        lines.push('')
        for (const u of urls) lines.push(`- ${u}`)
        lines.push('')
      }
    }
  }

  // ── completed audits: the LLM's own per-page instructions, deduped ──
  if (completed.length) {
    let taskNum = 1
    for (const priority of PRIORITY_ORDER) {
      const forPriority = completed.flatMap(a =>
        (a.results!.claude_code_instructions || [])
          .filter(i => i.priority === priority)
          .map(i => ({ ...i, page_url: a.page_url }))
      )
      if (!forPriority.length) continue

      lines.push(`## ${PRIORITY_LABELS[priority]} (${forPriority.length})`)
      lines.push('')
      for (const instr of forPriority) {
        lines.push(`${taskNum++}. **${instr.title}**`)
        lines.push(`   Pagina: ${instr.page_url}`)
        lines.push(`   → ${instr.prompt}`)
      }
      lines.push('')
    }
  }

  lines.push('---')
  lines.push(
    `Na de fix: klik "Analyseer alle pagina's" op de Content-tab om te bevestigen dat de ` +
    `getroffen pagina's weer normale content-audits opleveren.`
  )

  return lines.join('\n')
}
